import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAgentConfirmMessageApi } from '@hero/api-hooks';
import { HourglassIcon, LoaderIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';
import { useAgentChatStore } from './store';
import { humaniseKey } from './CardFields';

/**
 * One-line offer for confirmation cards left open by an earlier page
 * load — "2 drafts are waiting for you · Review / Discard".
 *
 * Why a strip and not a replayed thread: the panel's empty state (the
 * prompt chips) only renders on a fresh conversation, and that's the
 * screen most sessions should start on. Restoring the transcript to
 * rescue a stale draft would cost every user their starting point.
 * The strip keeps both — prompts below, unfinished work on top,
 * nothing entering the conversation until the user takes it up.
 *
 * Review hands the cards to the pending stack (the same UI the live
 * turn renders). Discard declines them server-side so the agent stops
 * counting them as open on the next turn.
 */
export const ResumeStrip = () => {
  const { t } = useTranslation('m-agent');
  const resumable = useAgentChatStore(s => s.resumable);
  const acceptResume = useAgentChatStore(s => s.acceptResume);
  const dismissResume = useAgentChatStore(s => s.dismissResume);
  const confirmApi = useAgentConfirmMessageApi();
  const [isDiscarding, setIsDiscarding] = useState(false);

  if (!resumable) return null;

  const { conversationId, confirmations } = resumable;
  const count = confirmations.length;

  const title =
    count === 1
      ? t('resume.title_one', 'A draft is waiting for you')
      : t('resume.title_other', '{{count}} drafts are waiting for you', { count });

  const actions = [...new Set(confirmations.map(c => c.action))]
    .map(action =>
      t(`confirmation.action_badge.${action}`, { defaultValue: humaniseKey(action) }),
    )
    .join(' · ');

  const discard = async () => {
    setIsDiscarding(true);
    // Best-effort: a card that fails to decline stays open server-side
    // and will simply be offered again on the next load.
    await Promise.all(
      confirmations.map(pending =>
        confirmApi
          .mutateAsync({
            conversationId,
            confirmationId: pending.id,
            decision: 'decline',
            action: pending.action,
          })
          .catch(() => undefined),
      ),
    );
    setIsDiscarding(false);
    dismissResume();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
      className='flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2.5'
    >
      <span className='flex size-7 flex-none items-center justify-center rounded-lg bg-amber-100 text-amber-700'>
        <HourglassIcon className='size-4' />
      </span>
      <div className='min-w-0 flex-1 text-start'>
        <p className='truncate text-sm font-medium text-slate-800'>{title}</p>
        {actions && <p className='truncate text-xs text-slate-500'>{actions}</p>}
      </div>
      <Button
        variant='ghost'
        size='sm'
        onClick={discard}
        disabled={isDiscarding}
        className='text-slate-500'
      >
        {isDiscarding ? (
          <LoaderIcon className='size-3.5 animate-spin' />
        ) : (
          t('resume.discard', 'Discard')
        )}
      </Button>
      <Button size='sm' onClick={acceptResume} disabled={isDiscarding}>
        {t('resume.review', 'Review')}
      </Button>
    </motion.div>
  );
};
